import React, { useEffect, useRef, useState } from "react";
import { Globe, X, Check, RotateCcw } from "lucide-react";

// Languages prioritised for international / Erasmus students in France.
// Codes match Google Translate's expected language codes.
const LANGUAGES = [
  { code: "en", flag: "🇬🇧", name: "English", native: "English" },
  { code: "es", flag: "🇪🇸", name: "Español", native: "Español" },
  { code: "de", flag: "🇩🇪", name: "Deutsch", native: "Deutsch" },
  { code: "it", flag: "🇮🇹", name: "Italiano", native: "Italiano" },
  { code: "pt", flag: "🇵🇹", name: "Português", native: "Português" },
  { code: "ar", flag: "🇸🇦", name: "العربية", native: "العربية" },
  { code: "zh-CN", flag: "🇨🇳", name: "中文 (简体)", native: "中文" },
  { code: "ru", flag: "🇷🇺", name: "Русский", native: "Русский" },
  { code: "ja", flag: "🇯🇵", name: "日本語", native: "日本語" },
  { code: "ko", flag: "🇰🇷", name: "한국어", native: "한국어" },
];

const STORAGE_KEY = "preferred_lang";

let _scriptLoaded = false;
let _widgetReady = false;
const _readyCallbacks = [];

/**
 * Inject the Google Translate widget once.
 * The widget creates a hidden <select class="goog-te-combo"> that we drive programmatically.
 */
function ensureGoogleTranslate() {
  if (_scriptLoaded) return;
  _scriptLoaded = true;

  // Hide the default Google bar / banner / tooltip with CSS overrides
  const style = document.createElement("style");
  style.id = "google-translate-overrides";
  style.textContent = `
    .goog-te-banner-frame.skiptranslate,
    .goog-te-gadget-icon,
    .goog-te-balloon-frame,
    iframe.skiptranslate { display: none !important; }
    body { top: 0 !important; }
    #google_translate_element { position: absolute; left: -9999px; width: 0; height: 0; overflow: hidden; }
    .goog-tooltip, .goog-tooltip:hover { display: none !important; }
    .goog-text-highlight { background-color: transparent !important; box-shadow: none !important; }
  `;
  document.head.appendChild(style);

  // Hidden mount point for the widget
  const mount = document.createElement("div");
  mount.id = "google_translate_element";
  document.body.appendChild(mount);

  // Google calls this global callback when the script is ready
  window.googleTranslateElementInit = function () {
    try {
      // eslint-disable-next-line no-new
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "fr",
          includedLanguages: LANGUAGES.map((l) => l.code).join(","),
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        "google_translate_element"
      );
      _widgetReady = true;
      _readyCallbacks.forEach((cb) => { try { cb(); } catch (e) { /* ignore */ } });
      _readyCallbacks.length = 0;
    } catch (e) {
      // Network blocked or CSP issue — fallback path will be used
      console.warn("Google Translate init failed", e);
    }
  };

  const s = document.createElement("script");
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  s.defer = true;
  s.onerror = () => { /* fallback handled in setLanguage */ };
  document.body.appendChild(s);
}

function onWidgetReady(cb) {
  if (_widgetReady) cb();
  else _readyCallbacks.push(cb);
}

/**
 * Build the Google Translate PROXY URL for the current page.
 * Example: studenthubfrance.fr/dashboard → studenthubfrance-fr.translate.goog/dashboard?_x_tr_sl=fr&_x_tr_tl=en
 * This is a 100%-reliable fallback when the inline widget can't load.
 */
function buildTranslateProxyUrl(targetLang) {
  const { hostname, pathname, search, hash } = window.location;
  // Replace dots with hyphens (Google Translate proxy convention)
  const proxyHost = hostname.replace(/\./g, "-") + ".translate.goog";
  const params = new URLSearchParams(search);
  params.set("_x_tr_sl", "fr");
  params.set("_x_tr_tl", targetLang);
  params.set("_x_tr_hl", targetLang);
  params.set("_x_tr_pto", "wapp");
  return `https://${proxyHost}${pathname}?${params.toString()}${hash || ""}`;
}

function setLanguage(code) {
  // Special case: empty = back to French (original)
  if (!code) {
    const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = `googtrans=; ${expire}; path=/`;
    document.cookie = `googtrans=; ${expire}; path=/; domain=.${window.location.hostname}`;
    document.cookie = `googtrans=; ${expire}; path=/; domain=${window.location.hostname}`;
    // If we are on the translate.goog proxy, redirect back to the original site
    if (window.location.hostname.endsWith(".translate.goog")) {
      const originalHost = window.location.hostname.replace(".translate.goog", "").split("-").join(".");
      // Use only top-level original (e.g. studenthubfrance.fr) — best-effort
      const guess = window.location.hostname.replace(/-(fr|com|net|org|io|app)\.translate\.goog$/i, ".$1");
      const target = guess !== window.location.hostname ? guess : originalHost;
      window.location.href = `https://${target}${window.location.pathname}`;
      return;
    }
    window.location.reload();
    return;
  }

  // Try the inline widget first (fastest, in-place)
  let attempt = 0;
  const tryInline = () => {
    const select = document.querySelector("select.goog-te-combo");
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event("change"));
      return true;
    }
    attempt += 1;
    return false;
  };
  if (tryInline()) return;

  // Retry for up to ~3s while the widget loads
  const interval = setInterval(() => {
    if (tryInline()) {
      clearInterval(interval);
    } else if (attempt >= 15) {
      // Fallback: 3s+ and widget still missing → use Google's proxy URL
      clearInterval(interval);
      window.location.href = buildTranslateProxyUrl(code);
    }
  }, 200);
}

export default function LanguageHelper() {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [translating, setTranslating] = useState(false);
  const ref = useRef(null);

  // Inject Google Translate once on mount
  useEffect(() => {
    ensureGoogleTranslate();
  }, []);

  // Re-apply persisted language once the widget is ready (e.g. after a soft navigation)
  useEffect(() => {
    if (!picked) return;
    // Don't redirect on every mount — only re-apply via the inline widget if it is loaded.
    onWidgetReady(() => {
      const select = document.querySelector("select.goog-te-combo");
      if (select && select.value !== picked) {
        select.value = picked;
        select.dispatchEvent(new Event("change"));
      }
    });
  }, [picked]);

  // Close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const choose = (code) => {
    setPicked(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore quota */ }
    setTranslating(true);
    // setLanguage handles inline-widget path AND fallback to Google Translate proxy
    setLanguage(code);
    setTimeout(() => { setTranslating(false); setOpen(false); }, 1500);
  };

  const pickedLang = LANGUAGES.find((l) => l.code === picked);

  return (
    <div className="relative" ref={ref} data-testid="language-helper">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-violet-300 hover:text-violet-700 text-sm transition-colors shadow-sm"
        aria-label="Choisir une langue / Choose a language"
        aria-expanded={open}
        data-testid="language-helper-btn"
        translate="no"
      >
        <Globe size={14} />
        <span className="hidden sm:inline" translate="no">
          {pickedLang ? `${pickedLang.flag} ${pickedLang.code.split("-")[0].toUpperCase()}` : "🇫🇷 FR"}
        </span>
        <span className="sm:hidden" translate="no">{pickedLang?.flag || "🇫🇷"}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[300px] sm:w-[340px] rounded-2xl border border-slate-200 bg-white shadow-xl p-3 z-50 max-h-[80vh] overflow-y-auto"
          data-testid="language-helper-popover"
          role="dialog"
          aria-label="Language selector"
          translate="no"
        >
          <div className="flex items-start justify-between mb-3 px-1">
            <div>
              <p className="font-display font-semibold text-slate-900 text-sm">🌐 Translate this app</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Choose your language · Choisis ta langue</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" aria-label="Fermer">
              <X size={16} />
            </button>
          </div>

          {/* FR = original */}
          <button
            onClick={() => choose("")}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors mb-1.5 ${
              picked === "" ? "bg-violet-50 text-violet-700 border border-violet-200" : "hover:bg-slate-50 border border-transparent"
            }`}
            data-testid="lang-fr"
          >
            <span aria-hidden="true">🇫🇷</span>
            <span className="flex-1 text-left">Français <span className="text-[10px] text-slate-400 ml-1">(original)</span></span>
            {picked === "" ? <Check size={14} className="text-violet-600" /> : <RotateCcw size={11} className="text-slate-300" />}
          </button>

          <div className="grid grid-cols-2 gap-1.5">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => choose(l.code)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  picked === l.code ? "bg-violet-50 text-violet-700 border border-violet-200" : "hover:bg-slate-50 border border-transparent"
                }`}
                data-testid={`lang-${l.code}`}
                disabled={translating}
                dir={l.code === "ar" ? "rtl" : "ltr"}
              >
                <span aria-hidden="true">{l.flag}</span>
                <span className="flex-1 text-left truncate">{l.native}</span>
                {picked === l.code && <Check size={14} className="text-violet-600 shrink-0" />}
              </button>
            ))}
          </div>

          {translating && (
            <p className="text-[11px] text-violet-600 mt-3 text-center animate-pulse" data-testid="translating-status">
              Translating in progress…
            </p>
          )}

          <p className="text-[10px] text-slate-400 mt-3 leading-snug border-t border-slate-100 pt-2 px-1">
            Translation is powered by Google Translate (free, automatic). You may briefly be redirected to a `studenthubfrance-fr.translate.goog` URL — that's normal, your data and login session are preserved. Tip: technical terms (CAF, CROUS, APL) stay in French because they are official French institutions.
          </p>
        </div>
      )}
    </div>
  );
}
